import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';

// The endpoint's ceiling; it has no page or offset, so this is everything it can return
export const SEBI_FILINGS_LIMIT = 200;

/**
 * DRHPs under SEBI review and RHP-stage approvals:
 * GET /api/core/ipos/sebi-filings-approvals?limit=200. One request is the
 * whole list, which the page filters and pages itself.
 *
 * options.maxAgeMs: skip the request when data this recent is already loaded
 */
export const fetchSebiFilings = createAsyncThunk(
  'sebiFilings/fetchSebiFilings',
  async (_options, { rejectWithValue }) => {
    try {
      const response = await ipoService.getSebiFilingsApprovals({ limit: SEBI_FILINGS_LIMIT });
      if (response?.success === false) {
        return rejectWithValue(response.error || 'Failed to load SEBI filings');
      }
      const data = response?.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      return {
        items,
        // More than the endpoint will hand back means the oldest are missing
        totalFiltered: typeof data.total_filtered === 'number' ? data.total_filtered : items.length,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load SEBI filings');
    }
  },
  {
    condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
      const { status, fetchedAt } = getState().sebiFilings;
      // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
      if (status === 'loading') return false;
      return !(maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs);
    },
  }
);

const initialState = {
  items: [],
  totalFiltered: 0,
  fetchedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

export const sebiFilingsSlice = createSlice({
  name: 'sebiFilings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSebiFilings.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSebiFilings.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.totalFiltered = action.payload.totalFiltered;
        state.fetchedAt = Date.now();
      })
      .addCase(fetchSebiFilings.rejected, (state, action) => {
        // Keep the previous list so a failed refresh doesn't blank the page
        state.status = 'failed';
        state.error = action.payload || 'Failed to load SEBI filings';
      });
  },
});

export default sebiFilingsSlice.reducer;
